import axios, {
  AxiosError,
  type AxiosInstance,
  type CreateAxiosDefaults,
  type InternalAxiosRequestConfig,
} from 'axios';
import createInterceptor, {
  type AuthTokenUpdatedFn,
  type FetchAccessTokenFn,
  setAccessToken,
} from '@/modules/auth/model/auth.interceptor.ts';
import { useAuthStore } from '@/modules/auth/store/auth.store.ts';
import bus from '@/plugins/bus';

const shouldUseToken = (config: InternalAxiosRequestConfig) => {
  if (!config.url?.includes('://')) {
    // relative url
    return true;
  }

  if (config.baseURL && config.url?.startsWith(config.baseURL)) {
    // relative url because it start with baseURL
    return true;
  }

  if (config.url?.includes('api/auth/logout')) {
    // always put it on the logout endpoint
    return true;
  }

  return false;
};
const refreshUrl = 'https://analyze.getwhy.io/api/auth/refresh_token';
const fetchAccessToken: FetchAccessTokenFn = async (_axios: AxiosInstance) => {
  const response = await _axios.get(refreshUrl, {
    withCredentials: true,
  });
  const { data } = response;

  return { access_token: data.access_token, expires_in: data.expires_in };
};

const buildNumberChangeChecker = (() => {
  let lastBuild: string | null = null;
  return (build: string) => {
    if (!lastBuild) {
      lastBuild = build;
    }
    if (lastBuild !== build) {
      lastBuild = build;
      bus.emit('buildNumberChanged', build);
    }
  };
})();

const channel = new BroadcastChannel('reco__auth_token_refresh_channel');
try {
  channel.addEventListener('message', async (event) => {
    try {
      if (event.data && event.data.type === 'access_token') {
        setAccessToken({
          access_token: event.data.payload.access_token,
          expires_in: event.data.payload.expires_in,
        });
      }
    } catch (err) {
      console.error(err);
    }
  });
} catch (err) {
  console.error(err);
}

const createInstance = (config: CreateAxiosDefaults) => {
  const _axios: AxiosInstance = axios.create(config);

  const authTokenUpdated: AuthTokenUpdatedFn = (
    token: string,
    expires_in: number,
  ) => {
    const store = useAuthStore();
    store.setAuthData(expires_in);

    try {
      channel.postMessage({
        type: 'access_token',
        payload: {
          access_token: token,
          expires_in: expires_in,
        },
      });
    } catch (err) {}
  };

  const redirectLogin = () => {
    bus.emit('redirectLogin');
  };

  const interceptor = createInterceptor(
    _axios,
    authTokenUpdated,
    redirectLogin,
    fetchAccessToken,
    refreshUrl,
  );

  _axios.interceptors.request.use((origRequest: InternalAxiosRequestConfig) => {
    if (!shouldUseToken(origRequest)) {
      origRequest.headers.delete('Authorization');
      return origRequest;
    }

    return interceptor.request(origRequest);
  });

  _axios.interceptors.response.use(undefined, interceptor.response);

  _axios.interceptors.response.use(
    undefined,
    async (error: AxiosError<{ trace_id?: string | null }>) => {
      if (error?.response?.data?.trace_id) {
        console.warn('API error with Trace ID: ', error.response.data.trace_id);
        bus.emit('trace_id', error.response.data.trace_id);
      }
      return Promise.reject(error);
    },
  );

  _axios.interceptors.response.use((response) => {
    if (typeof response.headers['x-getwhy-build'] === 'string') {
      const build = response.headers['x-getwhy-build'];

      buildNumberChangeChecker(build);
    }
    return response;
  });

  return _axios;
};

export const http = createInstance({
  baseURL: import.meta.env.FRONTEND_SAFE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ErrorResponse {
  error: string;
  message?: string;
  trace_id?: string;
}
